/**
 * ResponseImage component specifically for handling enquiry responses image content.
 * This component manages file downloading and URL fetching for enquiry response image attachments.
 */
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from 'carbon-components-react';
import { Download20 } from '@carbon/icons-react';
import {
  getAttachmentPreviewUrlStart,
  resetAttachmentPreviewUrl,
  getAttachmentDownloadUrlStart,
  resetAttachmentDownloadUrl
} from 'redux/enquiries/actions';
import {
  selectAttachmentPreviewUrl,
  selectLoadingAttachmentPreviewUrl,
  selectAttachmentDownloadUrl,
  selectLoadingAttachmentDownloadUrl
} from 'redux/enquiries';
import { useComponentMount } from 'utilities/hooks.util';

// Forward ref to expose download function to parent components
export interface ResponseImageRef {
  downloadFile: () => void;
}

interface ResponseImageProps {
  fileId?: number;
  enquiryId?: number;
  imageUrl?: string;
  imageName?: string;
  className?: string;
  style?: React.CSSProperties;
  onImageLoad?: () => void;
  onImageError?: (error: any) => void;
}

const ResponseImage = forwardRef<ResponseImageRef, ResponseImageProps>(
  (
    {
      fileId,
      enquiryId,
      imageUrl,
      imageName,
      className = '',
      style = {},
      onImageLoad,
      onImageError
    },
    ref
  ) => {
    const history = useHistory();
    const queryParams = new URLSearchParams(history?.location.search);
    const shareId = queryParams.get('shareId') || '';

    const dispatch = useDispatch();
    const isLoadingPreviewUrl: any = useSelector(
      selectLoadingAttachmentPreviewUrl
    );
    const previewUrl: any = useSelector(selectAttachmentPreviewUrl);
    const isLoadingDownloadUrl: any = useSelector(
      selectLoadingAttachmentDownloadUrl
    );
    const downloadUrl: any = useSelector(selectAttachmentDownloadUrl);

    const [displayImageUrl, setDisplayImageUrl] = useState<string>('');
    const [downloadButtonClicked, setDownloadButtonClicked] = useState<boolean>(
      false
    );
    const [hasError, setHasError] = useState<boolean>(false);

    // Only mount Redux effects if using fileId/enquiryId approach
    useComponentMount(() => {
      if (fileId && enquiryId) {
        dispatch(resetAttachmentPreviewUrl());
        dispatch(resetAttachmentDownloadUrl());
      }
    });

    // Handle direct imageUrl usage
    useEffect(() => {
      if (imageUrl && !fileId) {
        setDisplayImageUrl(imageUrl);
      }
    }, [imageUrl, fileId]);

    const downloadFile = () => {
      if (fileId && enquiryId) {
        setDownloadButtonClicked(true);
        dispatch(getAttachmentDownloadUrlStart({ fileId, shareId, enquiryId }));
      }
    };

    // Expose download function to parent components
    useImperativeHandle(ref, () => ({
      downloadFile
    }));

    // Handle download URL response (only for fileId/enquiryId usage)
    useEffect(() => {
      if (
        fileId &&
        enquiryId &&
        !isLoadingDownloadUrl &&
        downloadUrl?.url &&
        downloadButtonClicked
      ) {
        const anchor = document.createElement('a');
        anchor.href = downloadUrl.url;
        anchor.download = imageName || 'image';
        anchor.click();
        setTimeout(() => {
          setDownloadButtonClicked(false);
        }, 2000);
      }
    }, [
      isLoadingDownloadUrl,
      downloadUrl,
      downloadButtonClicked,
      imageName,
      fileId,
      enquiryId
    ]);

    // Handle preview URL response (only for fileId/enquiryId usage)
    useEffect(() => {
      if (fileId && enquiryId && previewUrl && !isLoadingPreviewUrl) {
        setDisplayImageUrl(previewUrl.url);
      }
    }, [isLoadingPreviewUrl, previewUrl, fileId, enquiryId]);

    // Fetch preview URL when component mounts (only for fileId/enquiryId usage)
    useEffect(() => {
      if (fileId && fileId > 0 && enquiryId) {
        dispatch(getAttachmentPreviewUrlStart({ fileId, shareId, enquiryId }));
      }
    }, [fileId, shareId, dispatch, enquiryId]);

    const handleImageLoad = () => {
      setHasError(false);
      onImageLoad?.();
    };

    const handleImageError = (error: any) => {
      setHasError(true);
      onImageError?.(error);
    };

    // For fileId/enquiryId usage, show loading while fetching URL
    const showLoading =
      fileId && enquiryId
        ? isLoadingPreviewUrl || !displayImageUrl
        : !displayImageUrl;

    if (showLoading) {
      return (
        <div className={`response-image-wrapper ${className}`} style={style}>
          <div className="image-loading-message">
            <div>Loading image...</div>
          </div>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className={`response-image-wrapper ${className}`} style={style}>
          <div className="image-loading-message">
            <div>Unable to load image</div>
          </div>
        </div>
      );
    }

    return (
      <div className={`response-image-wrapper ${className}`} style={style}>
        <div className="response-image-container">
          <img
            src={displayImageUrl}
            alt={imageName || 'Response image'}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
          />
        </div>
      </div>
    );
  }
);

// Export a download button component for easy use in consumers
export const ResponseImageDownloadButton: React.FC<{
  responseImageRef: React.RefObject<ResponseImageRef>;
  isDownloading?: boolean;
  buttonProps?: any;
}> = ({ responseImageRef, isDownloading = false, buttonProps = {} }) => {
  const handleDownload = () => {
    responseImageRef.current?.downloadFile();
  };

  return (
    <Button
      kind="ghost"
      size="field"
      onClick={handleDownload}
      disabled={isDownloading}
      {...buttonProps}
    >
      <Download20 />
    </Button>
  );
};

ResponseImage.displayName = 'ResponseImage';

export default ResponseImage;
